/** Iter 113 — client-side mirror of backend routes/manual_pricing.py.
 * Computes the live booking price breakdown from base (server quote) + operator overrides. */

export const num = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

export function extraLineTotal(e, days) {
  if (e.free) return 0;
  const qty = Math.max(1, parseInt(e.qty || 1, 10));
  const price = Math.max(0, num(e.price) ?? 0);
  const unit = e.pricing === "per_day" ? price * Math.max(1, days) : price;
  return Math.round(unit * qty * 100) / 100;
}

const r2 = (v) => Math.round(v * 100) / 100;

export function computeManualPricing(base, spec, extras) {
  const days = Math.max(1, base.days || 1);
  const rentalCalc = r2(base.rental_subtotal || 0);
  let rental = rentalCalc;
  if (num(spec.rental_subtotal_override) !== null) rental = r2(Math.max(0, num(spec.rental_subtotal_override)));
  else if (num(spec.daily_rate_override) !== null) rental = r2(Math.max(0, num(spec.daily_rate_override)) * days);

  const routeCalc = r2(base.route_fee || 0);
  const routeFee = num(spec.route_fee_override) !== null ? r2(Math.max(0, num(spec.route_fee_override))) : routeCalc;
  const meetGreet = r2(base.meet_greet_fee || 0);
  const insCalc = r2(base.insurance_fee || 0);
  const insuranceFee = num(spec.insurance_price_override) !== null ? r2(Math.max(0, num(spec.insurance_price_override))) : insCalc;

  const extrasLines = (extras || []).map((e) => ({ ...e, fee: extraLineTotal(e, days) }));
  const extrasTotal = r2(extrasLines.reduce((s, e) => s + e.fee, 0));

  const charges = (spec.additional_charges || []).filter((c) => c.label && num(c.amount) !== null);
  const chargesTotal = r2(charges.reduce((s, c) => s + num(c.amount), 0));

  const subtotal = r2(rental + routeFee + meetGreet + insuranceFee + extrasTotal + chargesTotal);

  let discount = 0;
  const dv = num(spec.discount_value);
  if (spec.discount_type && dv > 0) {
    discount = spec.discount_type === "percent" ? r2((subtotal * dv) / 100) : r2(Math.min(subtotal, dv));
  }
  const taxable = r2(Math.max(0, subtotal - discount));
  const vatRate = num(spec.vat_rate_pct) || 0;
  const vat = vatRate > 0 ? r2((taxable * vatRate) / 100) : 0;

  const depCalc = r2(base.deposit || 0);
  const deposit = num(spec.deposit_override) !== null ? r2(Math.max(0, num(spec.deposit_override))) : depCalc;

  const grandCalc = r2(taxable + vat + (spec.deposit_in_total ? deposit : 0));
  const grand = num(spec.total_override) !== null ? r2(Math.max(0, num(spec.total_override))) : grandCalc;
  const paid = r2(Math.max(0, num(spec.amount_paid) ?? 0));
  const balance = r2(Math.max(0, grand - paid));

  return {
    days, rental, rentalCalc, dailyRate: days ? r2(rental / days) : rental,
    routeFee, routeCalc, meetGreet,
    insuranceFee, insCalc,
    extrasLines, extrasTotal,
    charges, chargesTotal,
    subtotal, discount, vatRate, vat,
    deposit, depCalc,
    grandCalc, grand, paid, balance,
    overrides: {
      rental: rental !== rentalCalc,
      route: routeFee !== routeCalc,
      insurance: insuranceFee !== insCalc,
      deposit: deposit !== depCalc,
      total: grand !== grandCalc,
    },
  };
}

/** Build the pricing payload the backend expects (ManualPricingSpec shape). */
export function buildPricingPayload(spec, extras) {
  return {
    daily_rate_override: num(spec.daily_rate_override),
    rental_subtotal_override: num(spec.rental_subtotal_override),
    route_fee_override: num(spec.route_fee_override),
    additional_charges: (spec.additional_charges || [])
      .filter((c) => c.label && num(c.amount) !== null)
      .map((c) => ({ label: c.label, amount: num(c.amount) })),
    discount_type: num(spec.discount_value) > 0 ? spec.discount_type || "fixed" : null,
    discount_value: num(spec.discount_value),
    vat_rate_pct: num(spec.vat_rate_pct),
    insurance_price_override: num(spec.insurance_price_override),
    extras: (extras || []).map((e) => ({
      id: e.id || null, name: e.name, price: num(e.price) ?? 0,
      pricing: e.pricing || "per_booking", qty: Math.max(1, parseInt(e.qty || 1, 10)),
      free: !!e.free, custom: !!e.custom,
    })),
    deposit_override: num(spec.deposit_override),
    deposit_in_total: !!spec.deposit_in_total,
    amount_paid: num(spec.amount_paid),
    payment_method: spec.payment_method || null,
    total_override: num(spec.total_override),
  };
}

export const emptyPricingSpec = () => ({
  daily_rate_override: "", rental_subtotal_override: "", route_fee_override: "",
  additional_charges: [], discount_type: "fixed", discount_value: "",
  vat_rate_pct: "", insurance_price_override: "",
  deposit_override: "", deposit_in_total: false,
  amount_paid: "", payment_method: "Cash", total_override: "",
});
